import React from 'react';
import StatusBadge from '@dashboard/components/StatusBadge';
import Tooltip from '@dashboard/components/Tooltip';
import checkIsTicketTypeOnSale from '@dashboard/services/event/checkIsTicketTypeOnSale';
import checkIsTicketTypeSoldOut from '@repo/checkout/lib/checkIsTicketTypeSoldOut';
import checkIsTicketTypeScheduled from '@repo/checkout/lib/checkIsTicketTypeScheduled';
import { TicketTypeAvailabilityStatus } from '@/dashboard/enums/tickets';
import checkIsTicketTypeNotOnSale from '@repo/checkout/lib/checkIsTicketTypeNotOnSale';
import checkIsTicketGroupSoldOut from '@repo/checkout/lib/checkIsTicketGroupSoldOut';
import checkIsTicketGroupOnSale from '@dashboard/services/event/checkIsTicketGroupOnSale';
import checkIsTicketGroupScheduled from '@repo/checkout/lib/checkIsTicketGroupScheduled';
import checkIsTicketGroupNotOnSale from '@repo/checkout/lib/checkIsTicketGroupNotOnSale';
import describeBumpRule from '@dashboard/scenes/PageEdit/scenes/PageProduct/components/OrderBumps/describeBumpRule';

export default function TicketTypeBadges({ page, ticketType, ticketGroup, allTicketTypes = [] }) {
  let primaryBadge = null;
  let hiddenBadge = null;
  let disabledBadge = null;

  if (ticketType.status === 'archived') {
    return (
      <div className="flex gap-x-1 flex-wrap items-center">
        <div>
          <Tooltip label="This ticket is shown as archived to customers">
            <StatusBadge status="archived" label="Archived" />
          </Tooltip>
        </div>
      </div>
    );
  }

  const isOnSale = checkIsTicketTypeOnSale({ page, ticketType, allTicketTypes });
  const isSoldOut = checkIsTicketTypeSoldOut(ticketType);
  const isUnavailable = ticketType.availabilityStatus === TicketTypeAvailabilityStatus.UNAVAILABLE;
  const { isScheduled, hasInvalidReference } = checkIsTicketTypeScheduled({
    ticketType,
    allTicketTypes,
  });

  const isNotOnSale = checkIsTicketTypeNotOnSale({
    ticketType,
    isCanceled: page.eventDetails?.status === 'canceled',
    isOnSale,
    hasInvalidReference,
    isScheduled,
  });

  if (isSoldOut) {
    primaryBadge = {
      content: <StatusBadge status="sold_out" label="Sold out" />,
      tooltip: 'This ticket no longer has availability',
    };
  } else if (isUnavailable) {
    primaryBadge = {
      content: <StatusBadge status="unavailable" label="Unavailable" />,
      tooltip: 'This ticket is shown as unavailable to customers',
    };
  } else if (ticketType.status === 'archived') {
    primaryBadge = {
      content: <StatusBadge status="archived" label="Archived" />,
      tooltip: 'This ticket is shown as archived to customers',
    };
  } else if (ticketType.availabilityStatus === 'available' && !isOnSale && isScheduled) {
    primaryBadge = {
      content: <StatusBadge status="scheduled" label="Scheduled" />,
      tooltip: 'This ticket is scheduled to go on sale',
    };
  } else if (ticketType.availabilityStatus === 'available' && isOnSale) {
    const isGroupSoldOut = ticketGroup
      ? checkIsTicketGroupSoldOut(ticketGroup, { excludeTicketTypes: true })
      : false;
    const isGroupUnavailable = ticketGroup?.availabilityStatus === 'unavailable';

    const isGroupOnSale = ticketGroup ? checkIsTicketGroupOnSale({ page, ticketGroup }) : false;
    const { isScheduled: isGroupScheduled, hasInvalidReference: isGroupHasInvalidReference } =
      ticketGroup
        ? checkIsTicketGroupScheduled({ ticketGroup })
        : { isScheduled: false, hasInvalidReference: false };

    const isGroupNotOnSale = ticketGroup
      ? checkIsTicketGroupNotOnSale({
          ticketGroup,
          isCanceled: page.eventDetails?.status === 'canceled',
          isOnSale: isGroupOnSale,
          isScheduled: isGroupScheduled,
          hasInvalidReference: isGroupHasInvalidReference,
        })
      : false;

    if (isGroupSoldOut) {
      primaryBadge = {
        content: (
          <StatusBadge
            status="on_sale"
            label="On sale"
            className="!bg-white dark:!bg-background !text-black dark:!text-foreground !border-black dark:!border-foreground line-through"
          />
        ),
        tooltip: 'This ticket is sold out because the group is sold out',
      };
    } else if (isGroupUnavailable) {
      primaryBadge = {
        content: (
          <StatusBadge
            status="on_sale"
            label="On sale"
            className="!bg-white dark:!bg-background !text-black dark:!text-foreground !border-black dark:!border-foreground line-through"
          />
        ),
        tooltip: 'This ticket is unavailable because the group is unavailable',
      };
    } else if (isGroupNotOnSale) {
      primaryBadge = {
        content: (
          <StatusBadge
            status="on_sale"
            label="On sale"
            className="!bg-white dark:!bg-background !text-black dark:!text-foreground !border-black dark:!border-foreground line-through"
          />
        ),
        tooltip: 'This ticket is not on sale because the group is not on sale',
      };
    } else {
      primaryBadge = {
        content: <StatusBadge status="on_sale" label="On sale" />,
        tooltip: 'This ticket is currently on sale',
      };
    }
  }

  if (ticketType.hidden) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket is hidden',
    };
  }

  if (hiddenBadge == null && ticketType.hideWhenNotOnSale && isNotOnSale) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket is hidden when not on sale',
    };
  }

  if (hiddenBadge == null && ticketType.hideWhenScheduled && isScheduled) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket is hidden when scheduled to go on sale',
    };
  }

  if (hiddenBadge == null && ticketType.hideWhenUnavailable && isUnavailable) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket is hidden when unavailable',
    };
  }

  if (hiddenBadge == null && ticketType.hideWhenSoldOut && isSoldOut) {
    hiddenBadge = {
      content: <StatusBadge status="hidden" label="Hidden" />,
      tooltip: 'This ticket is hidden when sold out',
    };
  }

  if (ticketType.status === 'disabled') {
    disabledBadge = {
      content: <StatusBadge status="disabled" label="Disabled" />,
      tooltip: 'Disabled tickets will not be shown to customers',
    };
  }

  const badges = [];
  // If no primary badge is set, show sale ended for enabled tickets
  if (!primaryBadge && ticketType.status === 'enabled') {
    primaryBadge = {
      content: <StatusBadge status="sale_ended" label="Not on sale" />,
      tooltip: 'This ticket is currently not on sale',
    };
  }

  if (!disabledBadge && hiddenBadge) badges.push({ key: 'hidden', ...hiddenBadge });
  if (primaryBadge) badges.push({ key: 'primary', ...primaryBadge });
  if (disabledBadge) badges.push({ key: 'disabled', ...disabledBadge });
  if (ticketType.showHideLogic?.enabled === true) {
    badges.push({
      key: 'conditional',
      content: <StatusBadge status="conditional" label="Conditional" />,
      tooltip: describeBumpRule(ticketType.showHideLogic, {
        ticketTypes: allTicketTypes,
        markInactiveTickets: true,
      }),
    });
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <div className="flex gap-x-1 flex-wrap items-center">
      {badges.map(badge => (
        <div key={badge.key}>
          {badge.tooltip ? <Tooltip label={badge.tooltip}>{badge.content}</Tooltip> : badge.content}
        </div>
      ))}
    </div>
  );
}
